// Vouchers section index — replaces the "everything on the gateway" antipattern.
// Lists the 8 voucher types as big entry cards + quick-link to the voucher LIST + recurring.

import { AppShell } from '@/components/AppShell';
import { KbdChip } from '@/components/primitives/KbdChip';
import { cn } from '@/lib/cn';

interface VoucherCard {
  label: string;
  href: string;
  shortcut: string;
  description: string;
  tone: 'in' | 'out' | 'adj' | 'corr';
}

const TYPES: VoucherCard[] = [
  { label: 'Sales',       href: '/vouchers/new/sales',       shortcut: 'F8',  description: 'Outward supply · invoice · IRN if e-Invoice eligible', tone: 'in' },
  { label: 'Purchase',    href: '/vouchers/new/purchase',    shortcut: 'F9',  description: 'Inward supply · vendor bill', tone: 'out' },
  { label: 'Receipt',     href: '/vouchers/new/receipt',     shortcut: 'F6',  description: 'Money in · bank or cash · auto bill-wise allocate', tone: 'in' },
  { label: 'Payment',     href: '/vouchers/new/payment',     shortcut: 'F5',  description: 'Money out · TDS deduct · 43B(h) MSME timeline', tone: 'out' },
  { label: 'Contra',      href: '/vouchers/new/contra',      shortcut: 'F4',  description: 'Bank ↔ cash or bank ↔ bank · book entry only', tone: 'adj' },
  { label: 'Journal',     href: '/vouchers/new/journal',     shortcut: 'F7',  description: 'Adjustment entry · any Dr/Cr pair · no cash or stock', tone: 'adj' },
  { label: 'Credit Note', href: '/vouchers/new/credit-note', shortcut: 'Alt+F6', description: 'Sales return · price reduction · IRN cancellation', tone: 'corr' },
  { label: 'Debit Note',  href: '/vouchers/new/debit-note',  shortcut: 'Alt+F5', description: 'Purchase return · supplier debit · GSTR mismatch fix', tone: 'corr' },
];

// v1.6.0 — was 6 cobalt + 2 grey, which told the reader nothing. Colour now encodes
// DIRECTION OF MONEY, matching the Gateway quick-actions so the two surfaces agree:
//   in (emerald) · out (citrus) · adj (slate) · corr (cobalt)
const TONE_STYLES: Record<VoucherCard['tone'], string> = {
  in:   'border-emerald/40 bg-emerald/[0.05] hover:border-emerald hover:bg-emerald/10',
  out:  'border-citrus/40 bg-citrus/[0.05] hover:border-citrus hover:bg-citrus/10',
  adj:  'border-mist bg-haze hover:border-cool hover:bg-mist',
  corr: 'border-cobalt/40 bg-cobalt/[0.05] hover:border-cobalt hover:bg-cobalt/10',
};
const TONE_TEXT: Record<VoucherCard['tone'], string> = {
  in:   'text-emerald-ink',
  out:  'text-citrus-ink',
  adj:  'text-slate',
  corr: 'text-cobalt',
};

export function VouchersSectionPage() {
  return (
    <AppShell
      pageHeader={
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="font-display text-display-md font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
            Vouchers
          </h1>
          <span className="text-body-sm text-slate">Post · review · re-open · cancel.</span>
          <div className="ml-auto flex items-center gap-2">
            <a href="/vouchers/list" className="rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt hover:text-cobalt">
              All vouchers
            </a>
            <a href="/vouchers/recurring" className="rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt hover:text-cobalt">
              Recurring
            </a>
            <a href="/reports/day-book" className="rounded-md border border-mist bg-haze px-3 py-1.5 text-body-sm text-obsidian hover:border-cobalt hover:text-cobalt">
              Day Book
            </a>
          </div>
        </div>
      }
    >
      <section>
        <h2 className="mb-4 flex items-center text-micro font-medium uppercase tracking-[0.16em] text-obsidian">
          <span className="mr-3 inline-block h-[2px] w-6 bg-cobalt" />
          Post a new voucher
        </h2>
        <div className="orz-stagger grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TYPES.map((v) => (
            <a
              key={v.label}
              href={v.href}
              className={cn(
                'group flex flex-col gap-2 rounded-md border bg-frost p-5 transition-all duration-150',
                'hover:-translate-y-0.5 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-cobalt/40',
                TONE_STYLES[v.tone],
              )}
              style={{ backgroundImage: 'var(--wash-card)' }}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className={cn('font-display text-display-sm font-semibold', TONE_TEXT[v.tone])}>{v.label}</h3>
                <KbdChip>{v.shortcut}</KbdChip>
              </div>
              <p className="text-body-sm text-slate">{v.description}</p>
            </a>
          ))}
        </div>
      </section>
    </AppShell>
  );
}
